import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, MessageCircle, Loader2, PartyPopper } from 'lucide-react';

const WHATSAPP_NUMBER = '447934514016';

// Detect language from browser
const getBrowserLanguage = (): 'pt-BR' | 'en-GB' => {
  const lang = navigator.language || 'en-GB';
  return lang.startsWith('pt') ? 'pt-BR' : 'en-GB';
};

const t = {
  'pt-BR': {
    verifying: 'Verificando pagamento...',
    success: 'Pagamento confirmado!',
    welcome: 'Bem-vindo ao KeepNota!',
    description: 'Sua assinatura está ativa. Agora você pode aproveitar todos os recursos do seu plano.',
    nextSteps: 'Próximos passos:',
    step1: 'Volte ao WhatsApp e envie uma mensagem para começar',
    step2: 'Mande fotos, áudios ou textos das suas notas fiscais',
    step3: 'A IA vai organizar tudo automaticamente',
    cta: 'Voltar ao WhatsApp',
    home: 'Ir para o início',
    confetti: '🎉',
  },
  'en-GB': {
    verifying: 'Verifying payment...',
    success: 'Payment confirmed!',
    welcome: 'Welcome to KeepNota!',
    description: 'Your subscription is now active. You can now enjoy all the features of your plan.',
    nextSteps: 'Next steps:',
    step1: 'Go back to WhatsApp and send a message to get started',
    step2: 'Send photos, audio or text of your receipts',
    step3: 'AI will organise everything automatically',
    cta: 'Back to WhatsApp',
    home: 'Go to home',
    confetti: '🎉',
  },
};

export default function UpgradeSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isVerifying, setIsVerifying] = useState(true);
  const lang = getBrowserLanguage();
  const texts = t[lang];

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    lang === 'pt-BR' ? 'Oi! Acabei de assinar!' : 'Hi! I just subscribed!'
  )}`;

  useEffect(() => {
    // Simulate verification delay for better UX
    // In production, you might want to verify the session with the backend
    const timer = setTimeout(() => {
      setIsVerifying(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [sessionId]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">{texts.verifying}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Nav */}
      <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        {/* Success Icon */}
        <div className="relative inline-block mb-8">
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-green-500" />
          </div>
          <span className="absolute -top-2 -right-2 text-4xl">{texts.confetti}</span>
          <span className="absolute -bottom-1 -left-3 text-3xl">
            <PartyPopper className="w-8 h-8 text-yellow-500" />
          </span>
        </div>

        {/* Message */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          {texts.success}
        </h1>
        <h2 className="text-xl text-green-600 dark:text-green-400 font-semibold mb-4">
          {texts.welcome}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-10 max-w-md mx-auto">
          {texts.description}
        </p>

        {/* Next Steps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg mb-8 text-left">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            {texts.nextSteps}
          </h3>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-bold">
                1
              </span>
              <span className="text-gray-600 dark:text-gray-300 pt-0.5">{texts.step1}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-bold">
                2
              </span>
              <span className="text-gray-600 dark:text-gray-300 pt-0.5">{texts.step2}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-bold">
                3
              </span>
              <span className="text-gray-600 dark:text-gray-300 pt-0.5">{texts.step3}</span>
            </li>
          </ol>
        </div>

        {/* CTA */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-green-500 text-white font-semibold px-8 py-4 rounded-full hover:bg-green-600 transition shadow-lg hover:shadow-xl text-lg"
        >
          <MessageCircle className="w-6 h-6" />
          {texts.cta}
        </a>

        <div className="mt-6">
          <Link
            to="/"
            className="text-gray-500 dark:text-gray-400 hover:underline"
          >
            {texts.home}
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        <p>© 2025 KeepNota by Fig & Rosa Ltd.</p>
      </footer>
    </div>
  );
}
